import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, Clock } from 'lucide-react';
import axios from 'axios';
import { useLang } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FOUNDER_PREVIEW =
  'https://customer-assets.emergentagent.com/job_monline-revival/artifacts/g7bf47d0_Screenshot_20240828_173010_Gallery-4x.png';

/** Animated number that counts up from 0 once visible. */
const Counter = ({ value, duration = 1400, prefix = '', suffix = '', decimals = 0 }) => {
  const [n, setN] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const shownRef = useRef(0);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Re-animate whenever the value changes (e.g. stats arrive after the section is already on screen).
  useEffect(() => {
    if (!visible) return;
    const from = shownRef.current;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const cur = from + (value - from) * eased;
      shownRef.current = cur;
      setN(cur);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, value, duration]);

  const display = decimals > 0 ? n.toFixed(decimals) : Math.round(n).toLocaleString();
  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

const FounderPromo = () => {
  const { t } = useLang();
  const [stats, setStats] = useState({ total_count: 0, total_amount_eur: 0, unique_donors: 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/donations/stats`, { timeout: 8000 });
        if (alive && data) setStats(data);
      } catch (e) {
        // Silent fail — the section still works with 0s
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      className="relative w-full bg-[#0a0608] py-20 md:py-28 overflow-hidden"
      data-testid="founder-promo"
    >
      {/* Soft gold radial */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            'radial-gradient(ellipse at center top, rgba(245,180,0,0.22) 0%, rgba(245,180,0,0) 55%)',
        }}
      />

      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-12 fade-up">
          <p className="font-nav text-[#f5b400] text-xs tracking-widest inline-flex items-center gap-2 justify-center">
            <Sparkles size={12} /> {t('founder.eyebrow')}
          </p>
          <h2 className="font-heading section-heading-white mt-3">{t('founder.title_top')}</h2>
          <h2 className="font-heading section-heading-gold">{t('founder.title_bottom')}</h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/80 font-body text-sm md:text-base">
            {t('founder.lead_part1')}{' '}
            <span className="text-[#f5b400] font-semibold">{t('founder.lead_emph')}</span>{' '}
            {t('founder.lead_part2_before')}{' '}
            <span className="text-[#f5b400] font-semibold">{t('founder.title_bottom')}</span>{' '}
            {t('founder.lead_part2_after')}{' '}
            <span className="italic">{t('founder.lead_never')}</span> {t('founder.lead_end')}
          </p>
        </div>

        {/* Combined preview: Founder title floating above the player character */}
        <div className="flex justify-center mb-14">
          <div className="relative max-w-[460px] w-full">
            {/* Pulsing gold glow */}
            <div
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div className="set-glow-pulse w-[75%] h-[75%] rounded-full" />
            </div>
            <img
              src={FOUNDER_PREVIEW}
              alt="The Founder title floats above a knight character in New Realm Online"
              className="relative w-full object-contain drop-shadow-[0_18px_40px_rgba(245,180,0,0.35)] set-float"
              draggable="false"
              loading="lazy"
              data-testid="founder-preview-img"
            />
            <p className="mt-4 text-center font-body text-xs md:text-sm text-white/65 max-w-sm mx-auto leading-relaxed">
              {t('founder.preview_caption_part1')}{' '}
              <span className="text-[#f5b400] font-semibold">{t('founder.title_bottom')}</span>{' '}
              {t('founder.preview_caption_part2')}
            </p>
          </div>
        </div>

        {/* Live counters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <div
            className="rounded-xl border border-white/10 bg-black/40 px-6 py-7 text-center hover:border-[#f5b400]/60 transition-colors"
            data-testid="founder-stat-donors"
          >
            <p className="font-nav text-[10px] tracking-widest text-white/55">{t('founder.stat_donors_label')}</p>
            <p className="font-heading text-white text-4xl md:text-5xl mt-2 leading-none">
              <Counter value={stats.unique_donors} />
            </p>
            <p className="mt-2 text-xs text-white/55 font-body">{t('founder.stat_donors_sub')}</p>
          </div>

          <div
            className="rounded-xl border border-[#f5b400]/40 bg-gradient-to-b from-[#1a1410]/70 to-black/70 px-6 py-7 text-center shadow-[0_0_30px_-15px_rgba(245,180,0,0.55)]"
            data-testid="founder-stat-raised"
          >
            <p className="font-nav text-[10px] tracking-widest text-[#f5b400]">{t('founder.stat_raised_label')}</p>
            <p className="font-heading text-[#f5b400] text-4xl md:text-5xl mt-2 leading-none">
              <Counter value={stats.total_amount_eur} prefix="€" decimals={2} />
            </p>
            <p className="mt-2 text-xs text-white/55 font-body">{t('founder.stat_raised_sub')}</p>
          </div>

          <div
            className="rounded-xl border border-white/10 bg-black/40 px-6 py-7 text-center hover:border-[#f5b400]/60 transition-colors"
            data-testid="founder-stat-count"
          >
            <p className="font-nav text-[10px] tracking-widest text-white/55">{t('founder.stat_count_label')}</p>
            <p className="font-heading text-white text-4xl md:text-5xl mt-2 leading-none">
              <Counter value={stats.total_count} />
            </p>
            <p className="mt-2 text-xs text-white/55 font-body inline-flex items-center gap-1">
              <Clock size={11} className="text-[#f5b400]" /> {t('founder.stat_count_sub')}
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/donate"
            data-testid="founder-cta-donate"
            className="group inline-flex items-center gap-2 bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs tracking-widest px-8 py-4 rounded-md transition-all shadow-[0_10px_30px_-10px_rgba(245,180,0,0.7)] hover:shadow-[0_15px_40px_-10px_rgba(245,180,0,0.9)]"
          >
            <Heart size={14} className="group-hover:scale-110 transition-transform" />
            {t('founder.cta')}
          </Link>
          <p className="mt-4 text-[11px] text-white/50 font-body max-w-md mx-auto">
            {t('founder.fine_print')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default FounderPromo;
